import { convertPapersToBibTex } from '../../utils/paper-formatting';
import { CorpusId } from '../../models/aliases';
import { events } from '../../analytics/events';
import { formatNumber } from '../../utils/text';
import { Paper } from '../../models/result-set';
import { PaperAuthors } from './PaperAuthors';
import { PaperComponent } from './PaperComponent';
import { PaperContentComponent } from './content/PaperContentComponent';
import { PaperReaction, Reaction } from '../../models/reactions';
import { PaperReactionControls } from '../PaperReactionControls';
import { PaperTitle } from './PaperTitle';
import { useNotify } from '../../hooks/useNotify';
import { useTrackedHandler } from '../../hooks/useTracking';

import { Checkbox, IconButton, Typography } from '@mui/material';
import { PropsWithChildren } from 'react';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import styled from 'styled-components';

interface PaperCardProps {
  paper: Paper;
  selected: boolean;
  onPaperSelectionChange: (corpusId: CorpusId, isSelect: boolean) => void;
  AdditionalControlsComponent?: PaperComponent;
  titleOverride?: JSX.Element | null;
  isReadOnly: boolean;
  reactionEntry: PaperReaction | undefined;
  onReactionChanged?: (
    corpusId: CorpusId,
    reaction: Reaction | undefined,
  ) => void;
  onTextFeedbackSubmitted?: (corpusId: CorpusId, text: string) => void;
}

export function PaperCard(props: PaperCardProps): JSX.Element {
  const {
    paper,
    selected,
    onPaperSelectionChange,
    AdditionalControlsComponent,
    titleOverride,
    isReadOnly,
    reactionEntry,
    onReactionChanged = () => {},
    onTextFeedbackSubmitted = () => {},
  } = props;

  const notify = useNotify();

  const handleCiteThisPaperClick = useTrackedHandler(
    events.export.ExportCitationClicked,
    async () => {
      const bibText = convertPapersToBibTex([paper]);
      if (!navigator.clipboard) {
        return;
      }

      try {
        await navigator.clipboard.writeText(bibText);
        notify('Citation copied to clipboard', 'success');
      } catch (error) {
        notify('Error copying citation to clipboard', 'error');
      }
    },
    () => ({ corpusId: paper.corpusId }),
  );

  return (
    <PaperDiv key={paper.title}>
      <Checkbox
        disabled={isReadOnly}
        checked={selected}
        onChange={(e) =>
          onPaperSelectionChange(paper.corpusId, e.target.checked)
        }
      />
      <PaperContent>
        <TitleDiv>
          {titleOverride ?? (
            <PaperTitle title={paper.title} corpusId={paper.corpusId} />
          )}
          <Spacer />
          {AdditionalControlsComponent && !isReadOnly && (
            <AdditionalControlsComponent corpusId={paper.corpusId} />
          )}
        </TitleDiv>
        <AuthorsWrapper>
          <PaperAuthors
            corpusId={paper.corpusId}
            authors={paper.authors}
            authorLimit={4}
            shortenAuthorNames={false}
          />
          {paper.venue.length > 0 &&
            paper.venue.toLowerCase() !== 'unknown' && (
              <BulletedItem>{paper.venue}</BulletedItem>
            )}
          {paper.year > 0 && <BulletedItem>{paper.year}</BulletedItem>}
        </AuthorsWrapper>
        <PaperContentComponent paper={paper} />
        <FooterDiv>
          <FooterContentDiv>
            {paper.citationCount !== undefined && paper.citationCount > 0 && (
              <CitationDiv>
                <FormatQuoteIcon />
                <span>{`Cited by ${formatNumber(paper.citationCount)}`}</span>
              </CitationDiv>
            )}
            <CiteThisPaperButton onClick={handleCiteThisPaperClick}>
              <FormatQuoteIcon />
              Copy BibTeX
            </CiteThisPaperButton>
          </FooterContentDiv>
          {!isReadOnly && (
            <FeedbackDiv>
              <PaperReactionControls
                corpusId={paper.corpusId}
                reaction={reactionEntry?.reaction}
                onReactionChange={onReactionChanged}
                onTextFeedbackSubmitted={onTextFeedbackSubmitted}
              />
            </FeedbackDiv>
          )}
        </FooterDiv>
      </PaperContent>
    </PaperDiv>
  );
}

export type BulletedItemProps = PropsWithChildren;

export function BulletedItem(props: BulletedItemProps): JSX.Element {
  const { children } = props;
  return (
    <WrappedBulletSpan>
      <Typography component="p" variant="body2">
        •
      </Typography>
      <PaperMetaTypography>{children}</PaperMetaTypography>
    </WrappedBulletSpan>
  );
}

const PaperMetaTypography = styled(Typography).attrs({
  component: 'span',
  variant: 'subtitle1',
})`
  &.MuiTypography-root {
    font-size: 14px;
  }
`;

const FeedbackDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const WrappedBulletSpan = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const CiteThisPaperButton = styled(IconButton)`
  font-family: ${({ theme }) => theme.font.family.primary};

  &.MuiIconButton-root {
    padding: 0;
  }

  & .MuiSvgIcon-root {
    font-size: 18px;
    margin: 0px 4px;
  }
`;

const FooterContentDiv = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 2px 8px;
  gap: 10px;
`;

const FooterDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 28px;
  background-color: ${({ theme }) => theme.color.background.paper.secondary};
  border-radius: ${({ theme }) => theme.border.radius.mediumLarge};
`;

const CitationDiv = styled.div`
  color: rgba(250, 242, 233, 0.8);
  display: flex;
  align-items: center;
  gap: 3px;
  user-select: none;
  font-size: 14px;
  white-space: nowrap;
  & .MuiSvgIcon-root {
    font-size: 18px;
    margin: 0px 4px;
  }
`;

const AuthorsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
  padding: 5px 0px 3px 0px;
`;

const Spacer = styled.div`
  flex-grow: 1;
  display: flex;
`;

const TitleDiv = styled.div`
  display: flex;
  padding: 4px 0px;
`;

const PaperContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  position: relative;
`;

const PaperDiv = styled.div`
  margin-bottom: 5px;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 4px;

  .delete-button {
    position: absolute;
    top: 0;
    right: 0;
    visibility: hidden;
  }

  &:hover .delete-button {
    visibility: visible;
  }
`;
